import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, FileText, FolderDown } from 'lucide-react';

import { Alert, EmptyState } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { downloadUrl } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';

const KIND_LABELS: Record<string, string> = {
  document: 'Document',
  archive: 'Archive',
  code: 'Code source',
  dataset: 'Jeu de données',
  link: 'Lien externe',
  video: 'Vidéo',
  workflow: 'Workflow',
};

export function ResourcesPage() {
  const resources = useQuery({ queryKey: queryKeys.resources, queryFn: fetchers.resources });

  if (resources.isLoading) return <FullPageSpinner label="Chargement des ressources…" />;

  const items = resources.data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Ressources pédagogiques</h1>
        <p className="mt-1 text-sm text-muted">
          Les ressources des modules auxquels vous avez accès. Les autorisations sont vérifiées par
          l'API à chaque téléchargement.
        </p>
      </header>

      {items.length === 0 ? (
        <EmptyState
          icon={<FolderDown className="mx-auto h-8 w-8" />}
          title="Aucune ressource disponible"
          description="Les ressources apparaissent ici dès qu'un module auquel vous êtes inscrit en publie."
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {items.map((resource) => (
            <li key={resource.id}>
              <Card interactive className="h-full">
                <CardBody className="flex h-full flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <FileText className="h-5 w-5 shrink-0 text-tech" aria-hidden="true" />
                    <Badge tone="neutral">{KIND_LABELS[resource.kind] ?? resource.kind}</Badge>
                  </div>
                  <h2 className="mt-3 font-semibold text-fg">{resource.title}</h2>
                  {resource.description ? (
                    <p className="mt-1.5 flex-1 text-sm text-muted">{resource.description}</p>
                  ) : (
                    <div className="flex-1" />
                  )}

                  <div className="mt-4 border-t border-border pt-3">
                    {resource.external_url ? (
                      <a
                        href={resource.external_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-tech
                                   hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Consulter
                      </a>
                    ) : resource.has_file ? (
                      <a
                        href={downloadUrl(resource.id)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-tech
                                   hover:underline"
                      >
                        <Download className="h-4 w-4" />
                        Télécharger
                      </a>
                    ) : (
                      <span className="text-xs text-faint">
                        Fichier non encore déposé par le formateur
                      </span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Alert tone="info">
        Les ressources réservées aux formateurs (corrections, grilles d'évaluation) ne sont pas
        listées ici : l'API ne les renvoie pas à un compte candidat.
      </Alert>
    </div>
  );
}
